import { COMMANDS } from "$lib/commands/commands";
import { derived, get, readable, type Readable, writable } from "svelte/store";
import { notify, valueWritable } from "@ceyper-common";

import { file, interaction, frameIndex, frames, animationPlayer } from "$lib/state";
import { notImplemented } from "$lib/commands/common";

function delay<T>(store: Readable<T>, delay = 0) {
    return readable(get(store), (set) => {
        return store.subscribe((value) => setTimeout(() => set(value), delay));
    });
}

export function registerTimelineCommands() {
    const { add, destroy } = COMMANDS.registerActions("all");

    const hasFrames = (frames: any[] | null) => (frames?.length ?? 0) > 1;

    const isFileAvailable = derived(file, ($file) => !!$file);
    const isFrameCountGreaterThanOne = derived(frames, hasFrames);

    const isNotFirstFrame = derived(
        [frames, frameIndex],
        ([$frames, $frameIndex]) => hasFrames($frames) && ($frameIndex ?? 0) > 0
    );

    const isNotLastFrame = derived(
        [frames, frameIndex],
        ([$frames, $frameIndex]) => hasFrames($frames) && $frameIndex! < $frames!.length - 1
    );

    // const isAnimationAvailable = derived(
    //     [activeImage, activeAnimation],
    //     ([image, animation]) => !!(image && animation)
    // );
    //
    // const isAnimationUnavailable = derived([activeAnimation], ([animation]) => !animation);
    //
    // const playTimeout = valueWritable<any>(null);
    // const isPlayEnabled = delay(
    //     derived([activeAnimation, playTimeout], ([animation, timeout]) => !!(animation && !timeout))
    // );
    // const isPauseEnabled = delay(
    //     derived([activeAnimation, playTimeout], ([animation, timeout]) => !!(animation && timeout))
    // );

    /// Navigation

    add("animation.change-frame", changeFrame, isFrameCountGreaterThanOne);

    add(
        "animation.first-frame",
        () => {
            changeFrame(0);
        },
        isNotFirstFrame
    );

    add(
        "animation.next-frame",
        () => {
            const frame = frameIndex.value!;
            changeFrame(wrapFrame(frame + 1));
        },
        isFrameCountGreaterThanOne
    );

    add(
        "animation.prev-frame",
        () => {
            const frame = frameIndex.value!;
            changeFrame(wrapFrame(frame - 1));
        },
        isFrameCountGreaterThanOne
    );

    add(
        "animation.last-frame",
        () => {
            const frameCount = frames.value!.length;
            changeFrame(frameCount - 1);
        },
        isNotLastFrame
    );

    // Looping
    add(
        "animation.play",
        () =>
            animationPlayer.update((v) => ({
                ...v,
                playing: true,
            })),
        isFrameCountGreaterThanOne
    );

    add(
        "animation.pause",
        () =>
            animationPlayer.update((v) => ({
                ...v,
                playing: false,
            })),
        isFrameCountGreaterThanOne
    );

    /// Modify

    add(
        "animation.add-frame",
        (target: number = frameIndex.value!) => {
            file.value!.addFrame(undefined, target + 1);
        },
        isFileAvailable
    );

    add(
        "animation.duplicate-frame",
        (target: number = frameIndex.value!) => {
            file.value!.duplicateFrame(target);
        },
        isFileAvailable
    );

    add(
        "animation.remove-frame",
        (target: number = frameIndex.value!) => {
            if (frames.value!.length <= 1) {
                notify({
                    type: "message",
                    level: "warn",
                    id: "animation-remove-frame-error",
                    title: "Can't remove last frame",
                    message: `The last frame in an animation cannot be deleted.`,
                });
                return;
            }

            file.value!.removeFrame(target);
        },
        isFrameCountGreaterThanOne
    );

    add("animation.move-frame", moveFrame, isFrameCountGreaterThanOne);

    add(
        "animation.move-frame-left",
        () =>
            moveFrame(
                interaction.value.frameIndex.value,
                wrapFrame(interaction.value.frameIndex.value - 1),
                true
            ),
        isFrameCountGreaterThanOne
    );

    add(
        "animation.move-frame-right",
        () =>
            moveFrame(
                interaction.value.frameIndex.value,
                wrapFrame(interaction.value.frameIndex.value + 1),
                true
            ),
        isFrameCountGreaterThanOne
    );

    /// Subscriptions

    // activeImage.subscribe(imageChanged);
    //
    // appliedLayer.subscribe(pause);

    /// Helpers

    // function imageChanged() {
    //     if (!playTimeout) return;
    //     pause();
    // }
    //
    // async function play() {
    //     pause();
    //     COMMANDS.invoke("animation.next-frame");
    //     playTimeout.set(
    //         setTimeout(() => {
    //             play();
    //         }, activeImage.value!.duration) // $animation.delay[frame]
    //     );
    // }
    //
    // function pause() {
    //     clearTimeout(playTimeout.value);
    //     playTimeout.set(null);
    // }

    function clampFrame(frame: number) {
        const length = frames.value!.length;
        return Math.clamp(frame, 0, length - 1);
    }

    function wrapFrame(frame: number) {
        const length = frames.value!.length;
        return (frame + length) % length;
    }

    function changeFrame(frame: number) {
        interaction.subscribe(($int) => $int!.setFrame(frame))();
        interaction.value.redraw();
    }

    function moveFrame(from: number, to: number, setActive: boolean) {
        const frameCount = frames.value!.length;
        if (from === to) return;
        if (from < 0 || from >= frameCount) return;
        if (to < 0 || to >= frameCount) return;

        file.value!.moveFrame(from, to);

        if (setActive) changeFrame(to);
    }

    return { destroy };
}
